import React from 'react';
import { APITypes } from '../../../../../../primary-record-types';
import ProfileItem, { useChunkProfiles } from './ProfileItem';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import urls from '../../../../../../utils/urls';
import useMedicalProfiles from '../../../../../../hooks/api/useMedicalProfiles';
import { useCollaboratorInvitations } from '../../../../../../hooks/api/useCollaboratorInvitations';
import { getIntl } from '../../../../../../utils/intl';
import InvitationAlert, { getHasInvitations } from './InvitationAlert';
import RegistrationAddProfileModal from './RegistrationAddProfileModal';
import GuidanceContext from './GuidanceContext';
import ExternalInboundTransferRequestAlert from './ExternalInboundTransferRequestAlert';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import { useAddProfileButtonAction } from './useAddProfileButtonAction';
import useNotes from '../../../../../../hooks/api/useNotes';
import { useGuidanceAction } from '../../../../../../hooks/useGuidanceAction';
import MainContentContainer from '../../../../../v2BaseComponents/MainContentContainer';
import { AnimateLoadedContent } from '../../../../../v2BaseComponents/Loader';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import { useInboundExternalTransferRequest } from '../../../../../../hooks/api/useInboundTransferRequests';
import EmptyStateHint from '../../../../../shared/EmptyStateHint';
import { View } from 'react-native';
import SectionHeader from '../../../../../v2BaseComponents/SectionHeader';
import { useFindUserProfile } from '../../../../../../hooks/useUserHasOwnMedicalProfile';
import ClientInvitationAlert from './ClientInvitationAlert';
import { useMyOrganizationClientInvitations } from '../../../../../../hooks/api/useMyOrganizationClientInvitations';
import { useIsMobileLayout } from '../../../../../../hooks/useIsMobileLayout';

const intl = getIntl('profiles-landing', {
    profilesSharedWithMe: 'Profiles Shared With Me',
    profilesYouOrganize: 'Profiles You Organize',
    yourProfile: 'Your Profile',
    profiles: 'Profiles',
});

export type ConsumerAppProfilesLandingOwnProps = {
    isRegistration: boolean;
};

export type ConsumerAppLandingProps = ConsumerAppProfilesLandingOwnProps;

type CollaboratorItem =
    | APITypes.medicalProfileService.CollaboratorInviteListItem
    | APITypes.medicalProfileService.ProfileListItem;

const isInvite = (item: CollaboratorItem): item is APITypes.medicalProfileService.CollaboratorInviteListItem => {
    return (
        (item as APITypes.medicalProfileService.CollaboratorInviteListItem).medicalProfileCollaboratorInviteId !==
        undefined
    );
};

const ConsumerAppProfilesRoot: React.FC<ConsumerAppLandingProps> = (props) => {
    const isMobile = useIsMobileLayout();
    const [isRegistrationAddProfileModalOpen, setIsRegistrationAddProfileModalOpen] = React.useState(
        props.isRegistration,
    );
    const { isLoadingProfiles, medicalProfiles, collaboratorMedicalProfiles } = useMedicalProfiles();
    const selfProfile = useFindUserProfile()(medicalProfiles);
    const organizedProfiles = medicalProfiles.filter((_) => _.medicalProfileId !== selfProfile?.medicalProfileId);

    const { isLoading: isLoadingNotes } = useNotes({
        noteQueryArgs: {
            entityType: 'user',
        },
    });
    const addProfileAction = useAddProfileButtonAction();
    const { invitations } = useCollaboratorInvitations({});
    const profilesNavigation = useProfilesTabNavigation();
    const { hasInboundExternalTransferRequest } = useInboundExternalTransferRequest();
    const guidanceAction = useGuidanceAction({
        title: intl('profiles'),
        content: <GuidanceContext />,
    });
    const { isLoadingClientInvitations, clientInvitations } = useMyOrganizationClientInvitations({});

    const collaboratorItems: CollaboratorItem[] = [...collaboratorMedicalProfiles, ...invitations];

    const chunkedMedicalProfiles = useChunkProfiles(organizedProfiles);
    const chunkedCollaboratorProfiles = useChunkProfiles(collaboratorItems);

    const renderStandardProfileItem = (
        profile: APITypes.medicalProfileService.ProfileListItem,
        isCollaboratorProfile: boolean,
    ) => (
        <ProfileItem
            key={profile.medicalProfileId}
            profile={profile}
            onPress={() => {
                if (isMobile) {
                    profilesNavigation.navigate('profile', {
                        profileId: profile.medicalProfileId,
                        firstName: profile.preferredFirstName || profile.firstName || '',
                    });
                } else {
                    profilesNavigation.navigate('profile-activity', {
                        profileId: profile.medicalProfileId,
                    });
                }
            }}
            avatarUrl={urls.api.medicalProfiles.avatar(profile.medicalProfileId)}
            isCollaboratorProfile={isCollaboratorProfile}
        />
    );

    return (
        <>
            <RegistrationAddProfileModal
                isOpen={isRegistrationAddProfileModalOpen}
                onClose={() => setIsRegistrationAddProfileModalOpen(false)}
            />
            <AnimateLoadedContent isLoading={isLoadingNotes || isLoadingProfiles || isLoadingClientInvitations}>
                {() => (
                    <ActionButtonLayout
                        scrollable
                        actions={[addProfileAction.addProfileAction, guidanceAction.action]}
                        whiteBackground
                    >
                        <MainContentContainer>
                            <Stack vertical fullWidth space={20}>
                                {getHasInvitations(invitations) ? <InvitationAlert /> : null}
                                {!medicalProfiles.length &&
                                !collaboratorItems.length &&
                                !hasInboundExternalTransferRequest &&
                                !clientInvitations?.[0] ? (
                                    <EmptyStateHint variant="profiles" />
                                ) : null}
                                {clientInvitations?.[0] ? <ClientInvitationAlert /> : null}
                                {hasInboundExternalTransferRequest ? <ExternalInboundTransferRequestAlert /> : null}
                                {selfProfile ? (
                                    <Stack vertical fullWidth space={contentStackSpacing}>
                                        <SectionHeader text={intl('yourProfile')} />
                                        {renderStandardProfileItem(selfProfile, false)}
                                    </Stack>
                                ) : null}

                                {organizedProfiles.length ? (
                                    <Stack vertical fullWidth space={contentStackSpacing}>
                                        <SectionHeader text={intl('profilesYouOrganize')} />

                                        {chunkedMedicalProfiles.map((chunk, index) => (
                                            <Stack horizontal key={index} fullWidth space={contentStackSpacing}>
                                                {chunk.map((profile, index) => (
                                                    <View
                                                        style={{ flex: 1 }}
                                                        key={profile?.medicalProfileId + String(index)}
                                                    >
                                                        {profile ? renderStandardProfileItem(profile, false) : null}
                                                    </View>
                                                ))}
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : null}
                                {collaboratorItems.length ? (
                                    <Stack vertical fullWidth space={contentStackSpacing}>
                                        <SectionHeader text={intl('profilesSharedWithMe')} />
                                        {chunkedCollaboratorProfiles.map((chunk, index) => (
                                            <Stack horizontal key={index} fullWidth space={contentStackSpacing}>
                                                {chunk.map((profile, index) => (
                                                    <View
                                                        style={{ flex: 1 }}
                                                        key={profile?.medicalProfileId + String(index)}
                                                    >
                                                        {profile ? (
                                                            isInvite(profile) ? (
                                                                <ProfileItem
                                                                    profile={profile.medicalProfile}
                                                                    onPress={() => {
                                                                        profilesNavigation.navigate('invite', {
                                                                            profileId:
                                                                                profile.medicalProfile.medicalProfileId,
                                                                            invitationId:
                                                                                profile.medicalProfileCollaboratorInviteId,
                                                                        });
                                                                    }}
                                                                    avatarUrl={urls.api.collaborators.invitedProfileAvatar(
                                                                        profile.medicalProfile.medicalProfileId,
                                                                        profile.medicalProfileCollaboratorInviteId,
                                                                    )}
                                                                    isInvite
                                                                    isCollaboratorProfile
                                                                />
                                                            ) : (
                                                                renderStandardProfileItem(profile, true)
                                                            )
                                                        ) : null}
                                                    </View>
                                                ))}
                                            </Stack>
                                        ))}
                                    </Stack>
                                ) : null}
                            </Stack>
                        </MainContentContainer>
                    </ActionButtonLayout>
                )}
            </AnimateLoadedContent>
            {guidanceAction.Prompt}
            {addProfileAction.Prompt}
        </>
    );
};

export default React.memo(ConsumerAppProfilesRoot);
